export const AppArea = {
  ADMIN: "admin",
  AUTH: "auth",
  BIDS: "bids",
  CALENDAR: "calendar",
  CHANGE_ORDERS: "change_orders",
  CONTRACTORS: "contractors",
  CONVERSATIONS: "conversations",
  DEVELOPER: "developer",
  ESCALATIONS: "escalations",
  FINANCE: "finance",
  INSPECTION: "inspection",
  INSURANCE: "insurance",
  MY_QUEUE: "my_queue",
  OTHER: "other",
  PRODUCTION_SCHEDULE: "production_schedule",
  PROJECTS: "projects",
  QUOTING: "quoting",
  SETTINGS: "settings",
  TASKS: "tasks",
  TICKETS: "tickets",
  TODAYS_ACTIONS: "todays_actions",
} as const satisfies Record<string, string>;

export type AppArea = (typeof AppArea)[keyof typeof AppArea];

/**
 * Longest-prefix-first: `/dashboard/tasks/actions` must beat `/dashboard/tasks`,
 * and `/dashboard/bids` must beat `/dashboard/[projectId]`, which swallows every
 * one-segment child of `/dashboard`.
 */
const ROUTE_PREFIX_AREAS: readonly (readonly [string, AppArea])[] = [
  ["/dashboard/tasks/actions", AppArea.TODAYS_ACTIONS],
  ["/dashboard/production-schedule", AppArea.PRODUCTION_SCHEDULE],
  ["/dashboard/project-milestones", AppArea.PROJECTS],
  ["/dashboard/projectsv2", AppArea.PROJECTS],
  ["/dashboard/travelers-cleanup", AppArea.PROJECTS],
  ["/dashboard/my-organization", AppArea.SETTINGS],
  ["/dashboard/escalations", AppArea.ESCALATIONS],
  ["/dashboard/insurance", AppArea.INSURANCE],
  ["/dashboard/actionables", AppArea.TASKS],
  ["/dashboard/workflows", AppArea.TASKS],
  ["/dashboard/tasksv2", AppArea.TASKS],
  ["/dashboard/tasks", AppArea.TASKS],
  ["/dashboard/bids", AppArea.BIDS],
  ["/dashboard/rfqs", AppArea.QUOTING],
  ["/dashboard/dev", AppArea.DEVELOPER],
  ["/dashboard", AppArea.PROJECTS],
  ["/todays-actions", AppArea.TODAYS_ACTIONS],
  ["/tickets", AppArea.TICKETS],
  ["/inbox", AppArea.TICKETS],
  ["/conversations", AppArea.CONVERSATIONS],
  ["/quoting", AppArea.QUOTING],
  ["/quote", AppArea.QUOTING],
  ["/monitors/quoting", AppArea.QUOTING],
  ["/amfam/scope-of-work", AppArea.QUOTING],
  ["/contractors", AppArea.CONTRACTORS],
  ["/recruiting", AppArea.CONTRACTORS],
  ["/finance", AppArea.FINANCE],
  ["/financial-console", AppArea.FINANCE],
  ["/remittance", AppArea.FINANCE],
  ["/change-order", AppArea.CHANGE_ORDERS],
  ["/my-queue", AppArea.MY_QUEUE],
  ["/calendar", AppArea.CALENDAR],
  ["/dispatch", AppArea.CALENDAR],
  ["/live-photo-review", AppArea.INSPECTION],
  ["/roof", AppArea.INSPECTION],
  ["/property-model", AppArea.INSPECTION],
  ["/quote-scope", AppArea.INSPECTION],
  ["/admin", AppArea.ADMIN],
  ["/settings", AppArea.SETTINGS],
  ["/preferences", AppArea.SETTINGS],
  ["/profile", AppArea.SETTINGS],
  ["/notifications", AppArea.SETTINGS],
  ["/sign-in", AppArea.AUTH],
  ["/sign-up", AppArea.AUTH],
  ["/unauthorized", AppArea.AUTH],
  ["/developer-portal", AppArea.DEVELOPER],
  ["/devtools", AppArea.DEVELOPER],
  ["/playground", AppArea.DEVELOPER],
  ["/docs", AppArea.DEVELOPER],
  ["/guides", AppArea.DEVELOPER],
];

/**
 * Takes a Next.js route template (`router.pathname`), not a resolved URL — a
 * template keeps the PostHog breakdown at one row per route instead of one per
 * project id.
 */
export function appAreaFromRoute(route: string): AppArea {
  const match = ROUTE_PREFIX_AREAS.find(([prefix]) => route === prefix || route.startsWith(`${prefix}/`));

  return match?.[1] ?? AppArea.OTHER;
}
